import { type CSSProperties, type JSX, type ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { Sinnbild } from './Sinnbild';
import { useSchwebe } from './schwebe';

/**
 * Warum eine Wahl gesperrt ist, und wann es sie gibt (Auftrag #33).
 *
 * `grund` ist der Fall von jetzt („Quoten addieren sich nicht zu einem
 * Ganzen."), `wann` die Bedingung als ganzer Satz („Den Kreis gibt es, wenn
 * …"). Erst beides zusammen sagt, was man ändern muss.
 */
export type Sperre = { readonly grund: string; readonly wann: string };

export type Segmentoption<K extends string> = {
  readonly wert: K;
  readonly text: string;
  readonly vorne?: ReactNode;
  /** Gesetzt: Die Wahl steht da, ist aber nicht wählbar (Auftrag #33). */
  readonly gesperrt?: Sperre;
};

/**
 * DER SEGMENTREITER DES ENTWURFS (ÄA-41) — die Wahl „Anruf · E-Mail ·
 * Aufgabe · Termin" in der Aktivitätsspalte: eine graue Leiste (#eef1f6),
 * darin die Wahlen, die gewählte weiß mit Schrift in #1857a8.
 *
 * In Listen trägt er eine einfache, sich ausschließende Wahl („Alle · Meine").
 * Eine Wahl mit mehreren Häkchen ist er nicht — dafür gibt es Filtermarken.
 *
 * Die Wahlen sind Knöpfe mit `aria-pressed` in einer benannten Gruppe: Wer
 * mit Tab hineingeht, hört „Betreuung, Gruppe" und je Knopf, ob er gedrückt
 * ist (WCAG 4.1.2).
 *
 * GESPERRTE WAHLEN (Auftrag #33). Eine Wahl, die gerade nicht passt, steht
 * trotzdem da — wer sie nicht sieht, sucht sie und weiß nicht, dass es sie
 * gibt. Sie ist sichtbar gesperrt und sagt, warum und wann es sie gibt.
 */
export function Segmente<K extends string>({
  beschriftung,
  optionen,
  gewaehlt,
  beiWahl,
  leicht = false,
}: {
  readonly beschriftung: string;
  readonly optionen: readonly Segmentoption<K>[];
  readonly gewaehlt: K;
  readonly beiWahl: (wert: K) => void;
  /** Auftrag CRM-54: das Schloss in Light, ohne graue Fläche (Berichtsseite). */
  readonly leicht?: boolean;
}): JSX.Element {
  return (
    <div className="segmente" role="group" aria-label={beschriftung}>
      {optionen.map((o) =>
        o.gesperrt === undefined ? (
          <button
            key={o.wert}
            type="button"
            className={`segmente__wahl${o.wert === gewaehlt ? ' segmente__wahl--hier' : ''}`}
            aria-pressed={o.wert === gewaehlt}
            onClick={() => beiWahl(o.wert)}
          >
            {o.vorne}
            {o.text}
          </button>
        ) : (
          <GesperrteWahl
            key={o.wert}
            text={o.text}
            vorne={o.vorne}
            sperre={o.gesperrt}
            hier={o.wert === gewaehlt}
            leicht={leicht}
          />
        ),
      )}
    </div>
  );
}

/**
 * EINE GESPERRTE WAHL, AUF JEDEM WEG ERKLÄRT (Auftrag #33, NFA-07, NFA-14).
 *
 *   MAUS — Überfahren öffnet den Hinweis als Schwebefenster, mit Absicht und
 *   Nachlauf wie die Gruppen der Menüzeile (`schwebe.ts`, Auftrag #21).
 *
 *   TASTATUR — `aria-disabled` statt `disabled`: Der Knopf bleibt im
 *   Tabulatorweg, und der Hinweis hängt per `aria-describedby` daran; ein
 *   Vorleseprogramm liest „Kreis, gedimmt" und danach den Grund. Enter oder
 *   Leertaste zeigen ihn auch sichtbar.
 *
 *   FINGER — Tippen heftet den Hinweis an und schaltet nichts um. Ein Tippen
 *   daneben oder Escape nimmt ihn weg.
 *
 * Der Hinweis steht immer im DOM, zugeklappt mit `hidden`: So zeigt
 * `aria-describedby` nie ins Leere, und die Beschreibung gilt auch, solange
 * nichts zu sehen ist.
 */
function GesperrteWahl({
  text,
  vorne,
  sperre,
  hier,
  leicht,
}: {
  readonly text: string;
  readonly vorne: ReactNode;
  readonly sperre: Sperre;
  readonly hier: boolean;
  readonly leicht: boolean;
}): JSX.Element {
  const [angeheftet, setzeAngeheftet] = useState(false);
  const [schwebend, setzeSchwebend] = useState(false);
  const sichtbar = angeheftet || schwebend;
  const hinweisId = useId();
  const huelle = useRef<HTMLSpanElement>(null);
  const knopf = useRef<HTMLButtonElement>(null);
  const hinweis = useRef<HTMLSpanElement>(null);

  const schwebe = useSchwebe({
    oeffnen: () => setzeSchwebend(true),
    schliessen: () => setzeSchwebend(false),
  });

  function schliessen(): void {
    schwebe.anhalten();
    setzeSchwebend(false);
    setzeAngeheftet(false);
  }

  /* Escape nimmt ihn weg (WCAG 1.4.13); ein Druck daneben auch. */
  useEffect(() => {
    if (!sichtbar) return;
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      schliessen();
    };
    const beiDruck = (e: PointerEvent): void => {
      if (huelle.current?.contains(e.target as Node) === true) return;
      schliessen();
    };
    window.addEventListener('keydown', beiTaste);
    document.addEventListener('pointerdown', beiDruck);
    return () => {
      window.removeEventListener('keydown', beiTaste);
      document.removeEventListener('pointerdown', beiDruck);
    };
  });

  /*
   * Der Hinweis hängt unter der Wahl und bleibt im Bild: rechtsbündig, wo
   * links Platz ist, sonst an den linken Rand geschoben; nach oben, wenn
   * unten der Platz fehlt. Gemessen vor dem Zeichnen, damit nichts springt.
   */
  const [lage, setzeLage] = useState<CSSProperties>({ visibility: 'hidden' });
  useLayoutEffect(() => {
    if (!sichtbar) {
      setzeLage({ visibility: 'hidden' });
      return;
    }
    const RAND = 8;
    const ABSTAND = 6;
    const legen = (): void => {
      const r = knopf.current?.getBoundingClientRect();
      const h = hinweis.current;
      if (r === undefined || h === null) return;
      const breite = h.offsetWidth;
      const hoehe = h.offsetHeight;
      const left = Math.min(Math.max(RAND, r.right - breite), Math.max(RAND, window.innerWidth - RAND - breite));
      const unten = r.bottom + ABSTAND;
      const top = unten + hoehe <= window.innerHeight - RAND ? unten : Math.max(RAND, r.top - ABSTAND - hoehe);
      setzeLage({ top: `${top}px`, left: `${left}px` });
    };
    legen();
    window.addEventListener('resize', legen);
    window.addEventListener('scroll', legen, true);
    return () => {
      window.removeEventListener('resize', legen);
      window.removeEventListener('scroll', legen, true);
    };
  }, [sichtbar]);

  return (
    <span
      ref={huelle}
      className="segmente__sperre"
      /* Die Hülle umfasst Knopf und Hinweis: Die Maus im Hinweis hält ihn offen. */
      onPointerEnter={(e) => schwebe.eintritt(e, sichtbar)}
      onPointerLeave={(e) => schwebe.austritt(e)}
    >
      <button
        ref={knopf}
        type="button"
        className={`segmente__wahl segmente__wahl--gesperrt${hier ? ' segmente__wahl--hier' : ''}`}
        aria-pressed={hier}
        aria-disabled="true"
        aria-describedby={hinweisId}
        onClick={() => {
          /* Nie umschalten — nur den Hinweis zeigen oder wegnehmen. */
          schwebe.anhalten();
          setzeAngeheftet((a) => !a);
        }}
      >
        {vorne}
        {text}
        {leicht ? (
          <Sinnbild gewicht="light" name="lock-simple" klasse="segmente__schloss" />
        ) : (
          <Sinnbild name="lock-simple" klasse="segmente__schloss" />
        )}
      </button>
      <span ref={hinweis} id={hinweisId} role="tooltip" className="segmente__hinweis" hidden={!sichtbar} style={lage}>
        <strong>„{text}" ist gesperrt.</strong> {sperre.grund} {sperre.wann}
      </span>
    </span>
  );
}
